/**
 * AnnotationWaterSignsBuilder.ts — places Polish inland-waterway signs
 * on a dark 1.5 m post at authored point items (TerrainItemType slug
 * `water_sign`, procedural_key `water_sign`).
 *
 * Visual is fully procedural (vertex-coloured low-poly): post (cylinder)
 * + plate (box) + 2-D coloured rectangles drawn on the plate's front
 * face that compose the sign glyph and an optional numeric value.
 *
 * StaticMeshPass groups are keyed by `${code}|${value}` so identical
 * signs (e.g. five "1.8 m depth" boards) share one VBO/IBO and only
 * differ in per-instance transform.
 *
 * Mounting variant for GLB-embedded signs (future): the same template
 * generator is exposed so a follow-up builder can attach plate-only
 * meshes (no post) at named empty nodes inside e.g. a bridge GLB.
 */

import type { MeshAsset } from "../assets/MeshAsset";
import { MESH_VERTEX_FLOATS, MESH_VERTEX_STRIDE } from "../assets/MeshAsset";
import { createCylinderY } from "../assets/ProceduralAssets";
import { sampleTerrainHeight, type TerrainAsset } from "../assets/TerrainAsset";
import type { Mat4, StaticMeshPass } from "../passes/StaticMeshPass";

const POST_HEIGHT_M = 1.5;
const POST_RADIUS_M = 0.04;
const POST_COLOR: RGB = [0.08, 0.08, 0.09];

const PLATE_SIZE_M = 0.5;
const PLATE_THICKNESS_M = 0.04;
const PLATE_BORDER_M = 0.045;
/** Vertical offset of plate centre above the post top. */
const PLATE_CENTRE_Y_M = POST_HEIGHT_M + PLATE_SIZE_M * 0.5;

const GROUP_PREFIX = "annotation.water_sign";

type RGB = [number, number, number];

const COL_WHITE: RGB = [0.97, 0.97, 0.95];
const COL_BLACK: RGB = [0.04, 0.04, 0.04];
const COL_RED: RGB = [0.86, 0.1, 0.12];
const COL_BLUE: RGB = [0.07, 0.25, 0.55];
const COL_YELLOW: RGB = [0.98, 0.83, 0.05];

interface PlateTemplate {
  /** Plate background colour (the box body). */
  background: RGB;
  /** Optional coloured border drawn as a frame on the front face. */
  border?: { color: RGB; thickness?: number };
  /** Glyph decals drawn on the front face (plate-local 2-D, origin at centre). */
  glyph: Decal[];
  /** When true, the numeric value is drawn under/with the glyph. */
  showValue?: boolean;
  /** Override value colour (defaults to black). */
  valueColor?: RGB;
}

interface Decal {
  /** Plate-local X centre in metres (-PLATE/2 .. +PLATE/2). */
  cx: number;
  /** Plate-local Y centre in metres (origin at plate centre, +Y is up). */
  cy: number;
  /** Width and height in metres. */
  w: number;
  h: number;
  color: RGB;
  /** Optional rotation in degrees inside the plate plane (CCW). */
  rotDeg?: number;
}

// ─── Public builder ──────────────────────────────────────────────────────

export class AnnotationWaterSignsBuilder {
  constructor(private readonly staticMeshPass: StaticMeshPass) {}

  build(terrain: TerrainAsset): void {
    const items = extractWaterSignItems(terrain);
    if (items.length === 0) return;

    for (const item of items) {
      const groupId = `${GROUP_PREFIX}.${item.code}|${item.valueStr}`;
      this.ensureGroup(groupId, item.code, item.valueStr);
      const y = Math.max(sampleTerrainHeight(terrain, item.x, item.z), 0.0);
      this.staticMeshPass.addInstance(
        groupId,
        trsY(1, 1, 1, item.yaw, item.x, y, item.z),
      );
    }
    console.log(`[WaterSigns] placed ${items.length} sign(s)`);
  }

  private ensureGroup(id: string, code: string, valueStr: string): void {
    // StaticMeshPass.addGroup is idempotent on id; the second call is a no-op.
    this.staticMeshPass.addGroup(id, buildSignMesh(code, valueStr, true), 0, {
      castsShadow: true,
      receivesShadow: true,
    });
  }
}

// ─── Item extraction ─────────────────────────────────────────────────────

interface ResolvedItem {
  x: number;
  z: number;
  yaw: number;
  code: string;
  valueStr: string;
}

function extractWaterSignItems(terrain: TerrainAsset): ResolvedItem[] {
  const out: ResolvedItem[] = [];
  for (const obj of terrain.worldObjects) {
    if (obj.type !== "water_sign") continue;
    const [x, z] = obj.position;
    const props = (obj.properties ?? {}) as Record<string, unknown>;
    const codeRaw = typeof props.sign_code === "string" ? props.sign_code : "";
    const code = normaliseSignCode(codeRaw);
    if (!SIGN_TEMPLATES[code]) {
      console.warn(
        `[WaterSigns] unknown sign_code "${codeRaw}" → rendering as B8 (caution)`,
      );
    }
    const value = props.value;
    const valueStr =
      typeof value === "number" && Number.isFinite(value)
        ? formatValue(value)
        : typeof value === "string" && value.trim() !== ""
          ? value.trim()
          : "";
    const rotDeg =
      typeof props.rotation_deg === "number" ? props.rotation_deg : 0;
    // rotation_deg authored CW-from-north → convert to yaw around +Y (CCW
    // viewed from above). +Y in our world is up; sign of yaw matches trsY.
    const yaw = (-rotDeg * Math.PI) / 180;
    out.push({ x, z, yaw, code: SIGN_TEMPLATES[code] ? code : "B8", valueStr });
  }
  return out;
}

function normaliseSignCode(raw: string): string {
  return raw.replace(/[.\s_-]/g, "").toUpperCase();
}

function formatValue(n: number): string {
  // Strip trailing zeroes; "1.80" → "1.8", "12.0" → "12".
  return Number.isInteger(n) ? String(n) : String(parseFloat(n.toFixed(2)));
}

// ─── Sign templates ──────────────────────────────────────────────────────

const SIGN_TEMPLATES: Record<string, PlateTemplate> = {
  // A.1 — speed limit (km/h). White plate, red border, black digits.
  A1: {
    background: COL_WHITE,
    border: { color: COL_RED },
    glyph: [],
    showValue: true,
    valueColor: COL_BLACK,
  },
  // A.6 — no overtaking. Two up-arrows + red diagonal slash.
  A6: {
    background: COL_WHITE,
    border: { color: COL_RED },
    glyph: [
      // Left up-arrow (shaft + head).
      { cx: -0.06, cy: -0.02, w: 0.025, h: 0.18, color: COL_BLACK },
      { cx: -0.06, cy: 0.085, w: 0.07, h: 0.025, color: COL_BLACK, rotDeg: 45 },
      {
        cx: -0.06,
        cy: 0.085,
        w: 0.07,
        h: 0.025,
        color: COL_BLACK,
        rotDeg: -45,
      },
      // Right up-arrow.
      { cx: 0.06, cy: -0.02, w: 0.025, h: 0.18, color: COL_BLACK },
      { cx: 0.06, cy: 0.085, w: 0.07, h: 0.025, color: COL_BLACK, rotDeg: 45 },
      { cx: 0.06, cy: 0.085, w: 0.07, h: 0.025, color: COL_BLACK, rotDeg: -45 },
      // Red diagonal slash through the centre.
      { cx: 0, cy: 0, w: 0.42, h: 0.04, color: COL_RED, rotDeg: -45 },
    ],
  },
  // B.8 — caution. Black exclamation mark (vertical bar + dot).
  B8: {
    background: COL_WHITE,
    border: { color: COL_RED },
    glyph: [
      { cx: 0, cy: 0.04, w: 0.04, h: 0.22, color: COL_BLACK },
      { cx: 0, cy: -0.13, w: 0.05, h: 0.05, color: COL_BLACK },
    ],
  },
  // E.1 — water depth (m). Blue plate, white digits, downward white arrow.
  E1: {
    background: COL_BLUE,
    glyph: [
      { cx: 0, cy: 0.14, w: 0.035, h: 0.1, color: COL_WHITE },
      { cx: 0, cy: 0.07, w: 0.1, h: 0.035, color: COL_WHITE, rotDeg: 45 },
      { cx: 0, cy: 0.07, w: 0.1, h: 0.035, color: COL_WHITE, rotDeg: -45 },
    ],
    showValue: true,
    valueColor: COL_WHITE,
  },
  // E.2 — channel width (m). Black plate with white triangles pointing
  // inward (left ▸ and ◂ right) and a white digit between them.
  E2: {
    background: COL_BLACK,
    glyph: [
      // Left arrow ▸ (pointing right toward centre).
      { cx: -0.16, cy: 0, w: 0.07, h: 0.04, color: COL_WHITE, rotDeg: 30 },
      { cx: -0.16, cy: 0, w: 0.07, h: 0.04, color: COL_WHITE, rotDeg: -30 },
      // Right arrow ◂ (pointing left toward centre).
      { cx: 0.16, cy: 0, w: 0.07, h: 0.04, color: COL_WHITE, rotDeg: 150 },
      { cx: 0.16, cy: 0, w: 0.07, h: 0.04, color: COL_WHITE, rotDeg: -150 },
    ],
    showValue: true,
    valueColor: COL_WHITE,
  },
  // E.3 — clearance above water (m). Yellow plate with black down-arrows
  // bracketing a black digit.
  E3: {
    background: COL_YELLOW,
    glyph: [
      // Left down-arrow ▾.
      { cx: -0.16, cy: 0, w: 0.07, h: 0.035, color: COL_BLACK, rotDeg: 60 },
      { cx: -0.16, cy: 0, w: 0.07, h: 0.035, color: COL_BLACK, rotDeg: -60 },
      // Right down-arrow ▾.
      { cx: 0.16, cy: 0, w: 0.07, h: 0.035, color: COL_BLACK, rotDeg: 60 },
      { cx: 0.16, cy: 0, w: 0.07, h: 0.035, color: COL_BLACK, rotDeg: -60 },
    ],
    showValue: true,
    valueColor: COL_BLACK,
  },
};

// ─── Mesh assembly ───────────────────────────────────────────────────────

interface PartBuf {
  positions: number[]; // flat xyz
  normals: number[];
  colors: number[];
  indices: number[];
}

function newBuf(): PartBuf {
  return { positions: [], normals: [], colors: [], indices: [] };
}

function appendMesh(
  dst: PartBuf,
  src: MeshAsset,
  ox: number,
  oy: number,
  oz: number,
): void {
  const base = dst.positions.length / 3;
  const v = src.vertices;
  const stride = MESH_VERTEX_FLOATS;
  for (let i = 0; i < v.length; i += stride) {
    dst.positions.push(v[i] + ox, v[i + 1] + oy, v[i + 2] + oz);
    dst.normals.push(v[i + 3], v[i + 4], v[i + 5]);
    dst.colors.push(v[i + 6], v[i + 7], v[i + 8]);
  }
  for (let i = 0; i < src.indices.length; i++) {
    dst.indices.push(base + src.indices[i]);
  }
}

/** Append a flat coloured quad in the XY plane (normal +Z), origin at
 *  (cx, cy, z), rotated CCW by `rotDeg` inside the plane. */
function appendDecal(
  dst: PartBuf,
  cx: number,
  cy: number,
  z: number,
  w: number,
  h: number,
  rotDeg: number,
  color: RGB,
): void {
  const hw = w * 0.5;
  const hh = h * 0.5;
  const c = Math.cos((rotDeg * Math.PI) / 180);
  const s = Math.sin((rotDeg * Math.PI) / 180);
  const corners: Array<[number, number]> = [
    [-hw, -hh],
    [hw, -hh],
    [hw, hh],
    [-hw, hh],
  ];
  const base = dst.positions.length / 3;
  for (const [lx, ly] of corners) {
    const x = cx + c * lx - s * ly;
    const y = cy + s * lx + c * ly;
    dst.positions.push(x, y, z);
    dst.normals.push(0, 0, 1);
    dst.colors.push(color[0], color[1], color[2]);
  }
  dst.indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
}

function finalise(buf: PartBuf, label: string): MeshAsset {
  const vCount = buf.positions.length / 3;
  const out = new Float32Array(vCount * MESH_VERTEX_FLOATS);
  for (let i = 0; i < vCount; i++) {
    const o = i * MESH_VERTEX_FLOATS;
    out[o] = buf.positions[i * 3];
    out[o + 1] = buf.positions[i * 3 + 1];
    out[o + 2] = buf.positions[i * 3 + 2];
    out[o + 3] = buf.normals[i * 3];
    out[o + 4] = buf.normals[i * 3 + 1];
    out[o + 5] = buf.normals[i * 3 + 2];
    out[o + 6] = buf.colors[i * 3];
    out[o + 7] = buf.colors[i * 3 + 1];
    out[o + 8] = buf.colors[i * 3 + 2];
  }
  return {
    vertices: out,
    indices: new Uint32Array(buf.indices),
    stride: MESH_VERTEX_STRIDE,
    label,
  };
}

/** Builds the full sign mesh (post + plate + glyph) in local space.
 *  Post base sits at y = 0; plate centre at y = PLATE_CENTRE_Y_M facing +Z. */
function buildSignMesh(
  code: string,
  valueStr: string,
  includePost: boolean,
): MeshAsset {
  const tpl = SIGN_TEMPLATES[code] ?? SIGN_TEMPLATES.B8;
  const buf = newBuf();

  if (includePost) {
    const post = createCylinderY(POST_RADIUS_M, POST_HEIGHT_M, 10, POST_COLOR);
    appendMesh(buf, post, 0, 0, 0);
  }

  // Plate body (box) — built inline to keep vertex colours uniform.
  appendPlateBox(buf, tpl.background);

  // Front-face decals are stacked just in front of the plate to avoid
  // z-fighting. Border first, then glyph on top, then value.
  const frontZ = PLATE_THICKNESS_M * 0.5 + 0.001;
  const glyphZ = frontZ + 0.001;
  const valueZ = frontZ + 0.001;

  if (tpl.border) {
    appendBorderFrame(
      buf,
      frontZ,
      tpl.border.color,
      tpl.border.thickness ?? PLATE_BORDER_M,
    );
  }
  for (const d of tpl.glyph) {
    appendDecal(
      buf,
      d.cx,
      PLATE_CENTRE_Y_M + d.cy,
      glyphZ,
      d.w,
      d.h,
      d.rotDeg ?? 0,
      d.color,
    );
  }
  if (tpl.showValue && valueStr !== "") {
    appendNumber(buf, valueStr, valueZ, tpl.valueColor ?? COL_BLACK);
  }

  return finalise(buf, `water_sign:${code}|${valueStr}`);
}

function appendPlateBox(buf: PartBuf, color: RGB): void {
  const hw = PLATE_SIZE_M * 0.5;
  const hh = PLATE_SIZE_M * 0.5;
  const ht = PLATE_THICKNESS_M * 0.5;
  const cy = PLATE_CENTRE_Y_M;
  type Face = { n: RGB; pts: Array<[number, number, number]> };
  const faces: Face[] = [
    // Front (+Z).
    {
      n: [0, 0, 1],
      pts: [
        [-hw, cy - hh, ht],
        [hw, cy - hh, ht],
        [hw, cy + hh, ht],
        [-hw, cy + hh, ht],
      ],
    },
    // Back (-Z).
    {
      n: [0, 0, -1],
      pts: [
        [hw, cy - hh, -ht],
        [-hw, cy - hh, -ht],
        [-hw, cy + hh, -ht],
        [hw, cy + hh, -ht],
      ],
    },
    // Top (+Y).
    {
      n: [0, 1, 0],
      pts: [
        [-hw, cy + hh, -ht],
        [hw, cy + hh, -ht],
        [hw, cy + hh, ht],
        [-hw, cy + hh, ht],
      ],
    },
    // Bottom (-Y).
    {
      n: [0, -1, 0],
      pts: [
        [-hw, cy - hh, ht],
        [hw, cy - hh, ht],
        [hw, cy - hh, -ht],
        [-hw, cy - hh, -ht],
      ],
    },
    // Left (-X).
    {
      n: [-1, 0, 0],
      pts: [
        [-hw, cy - hh, -ht],
        [-hw, cy - hh, ht],
        [-hw, cy + hh, ht],
        [-hw, cy + hh, -ht],
      ],
    },
    // Right (+X).
    {
      n: [1, 0, 0],
      pts: [
        [hw, cy - hh, ht],
        [hw, cy - hh, -ht],
        [hw, cy + hh, -ht],
        [hw, cy + hh, ht],
      ],
    },
  ];
  for (const f of faces) {
    const base = buf.positions.length / 3;
    for (const p of f.pts) {
      buf.positions.push(p[0], p[1], p[2]);
      buf.normals.push(f.n[0], f.n[1], f.n[2]);
      buf.colors.push(color[0], color[1], color[2]);
    }
    buf.indices.push(base, base + 1, base + 2, base, base + 2, base + 3);
  }
}

/** Draw a hollow rectangular border on the plate front face. */
function appendBorderFrame(
  buf: PartBuf,
  z: number,
  color: RGB,
  thickness: number,
): void {
  const s = PLATE_SIZE_M;
  // Top, bottom, left, right strips.
  appendDecal(
    buf,
    0,
    PLATE_CENTRE_Y_M + (s - thickness) * 0.5,
    z,
    s,
    thickness,
    0,
    color,
  );
  appendDecal(
    buf,
    0,
    PLATE_CENTRE_Y_M - (s - thickness) * 0.5,
    z,
    s,
    thickness,
    0,
    color,
  );
  appendDecal(
    buf,
    -(s - thickness) * 0.5,
    PLATE_CENTRE_Y_M,
    z,
    thickness,
    s,
    0,
    color,
  );
  appendDecal(
    buf,
    (s - thickness) * 0.5,
    PLATE_CENTRE_Y_M,
    z,
    thickness,
    s,
    0,
    color,
  );
}

// ─── 7-segment numerals ──────────────────────────────────────────────────
//
// Each glyph is drawn in a 0.10 × 0.18 m cell. Stroke thickness ≈ 0.022 m.
// '.' is a small square at the bottom-right corner.

const DIGIT_W = 0.1;
const DIGIT_H = 0.18;
const DIGIT_STROKE = 0.022;
const DIGIT_GAP = 0.02;
const DOT_SIZE = 0.03;

interface SegmentMap {
  // a=top, b=top-right, c=bot-right, d=bottom, e=bot-left, f=top-left, g=middle.
  a?: boolean;
  b?: boolean;
  c?: boolean;
  d?: boolean;
  e?: boolean;
  f?: boolean;
  g?: boolean;
}

const DIGIT_SEGMENTS: Record<string, SegmentMap> = {
  "0": { a: true, b: true, c: true, d: true, e: true, f: true },
  "1": { b: true, c: true },
  "2": { a: true, b: true, g: true, e: true, d: true },
  "3": { a: true, b: true, g: true, c: true, d: true },
  "4": { f: true, g: true, b: true, c: true },
  "5": { a: true, f: true, g: true, c: true, d: true },
  "6": { a: true, f: true, g: true, e: true, c: true, d: true },
  "7": { a: true, b: true, c: true },
  "8": { a: true, b: true, c: true, d: true, e: true, f: true, g: true },
  "9": { a: true, b: true, c: true, d: true, f: true, g: true },
};

function drawDigit(
  buf: PartBuf,
  ch: string,
  cx: number,
  cy: number,
  z: number,
  color: RGB,
): void {
  if (ch === ".") {
    appendDecal(
      buf,
      cx,
      cy - DIGIT_H * 0.5 + DOT_SIZE * 0.5,
      z,
      DOT_SIZE,
      DOT_SIZE,
      0,
      color,
    );
    return;
  }
  const seg = DIGIT_SEGMENTS[ch];
  if (!seg) return;
  const w = DIGIT_W;
  const h = DIGIT_H;
  const t = DIGIT_STROKE;
  const halfW = w * 0.5 - t * 0.5;
  const halfH = h * 0.5 - t * 0.5;
  const midY = 0;
  const horiz = w - t; // horizontal stroke length
  const vert = h * 0.5 - t; // vertical stroke length per half
  if (seg.a) appendDecal(buf, cx, cy + halfH, z, horiz, t, 0, color);
  if (seg.d) appendDecal(buf, cx, cy - halfH, z, horiz, t, 0, color);
  if (seg.g) appendDecal(buf, cx, cy + midY, z, horiz, t, 0, color);
  if (seg.b) appendDecal(buf, cx + halfW, cy + h * 0.25, z, t, vert, 0, color);
  if (seg.c) appendDecal(buf, cx + halfW, cy - h * 0.25, z, t, vert, 0, color);
  if (seg.f) appendDecal(buf, cx - halfW, cy + h * 0.25, z, t, vert, 0, color);
  if (seg.e) appendDecal(buf, cx - halfW, cy - h * 0.25, z, t, vert, 0, color);
}

function charAdvance(ch: string): number {
  if (ch === ".") return DOT_SIZE + DIGIT_GAP;
  if (ch === "1") return DIGIT_STROKE + DIGIT_GAP * 1.5;
  return DIGIT_W + DIGIT_GAP;
}

/** Draws `text` centred horizontally at plate centre (y ≈ plate centre). */
function appendNumber(buf: PartBuf, text: string, z: number, color: RGB): void {
  // First pass: total width.
  let total = 0;
  for (let i = 0; i < text.length; i++) total += charAdvance(text[i]);
  total -= DIGIT_GAP; // no trailing gap
  let cursor = -total * 0.5;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    const adv = charAdvance(ch);
    const cx =
      cursor +
      (ch === "."
        ? DOT_SIZE * 0.5
        : ch === "1"
          ? DIGIT_STROKE * 0.5
          : DIGIT_W * 0.5);
    drawDigit(buf, ch, cx, PLATE_CENTRE_Y_M, z, color);
    cursor += adv;
  }
}

// ─── Helpers ─────────────────────────────────────────────────────────────

function trsY(
  sx: number,
  sy: number,
  sz: number,
  yaw: number,
  tx: number,
  ty: number,
  tz: number,
): Mat4 {
  const c = Math.cos(yaw);
  const s = Math.sin(yaw);
  // prettier-ignore
  return new Float32Array([
    c * sx, 0, -s * sx, 0,
    0,      sy, 0,      0,
    s * sz, 0, c * sz,  0,
    tx,     ty, tz,     1,
  ]) as Mat4;
}
